'use client';

/**
 * PlanPositionRoomDetailCard — Plan Position's rail detail: tier
 * totals, every undecided requirement by spread, and the room's own
 * room_story narrative paragraph, unedited.
 *
 * Reads graph-selection-store's inspectedEntity (type=='room', set by
 * ObeyaPlanPositionRoomsCard's row click) and re-resolves room_story
 * with the SAME params as the canvas rooms card — React Query dedupes
 * the fetch by queryKey, so opening this costs no extra BFF call.
 * Renders a placeholder prompt when nothing is selected, same
 * "closes in one gesture, carries nothing load-bearing" role the
 * design mockup this was scoped from gave the rail.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanPositionRoomDetailCard
 * @see klyde_edge/klyde_intelligence/projects/room_story.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Rail Insights / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

interface TierTotals {
  budget: number | null; mid: number | null; premium: number | null;
  resolved_requirements: number; unresolved_requirements: number;
}
interface RoomRequirement {
  requirement: string;
  candidate_price_spread: { min: number; max: number } | null;
  spread_type: 'undecided' | 'reopen' | null;
}
interface RoomStoryRow {
  key: string; label: string;
  vendor_count: number;
  requirements_total: number; requirements_selected: number; candidates_total: number;
  tier_totals: TierTotals;
  requirements: RoomRequirement[];
  insight: string;
}
interface RoomStoryData { rooms: RoomStoryRow[] }

const money = (v: number) => `$${Math.round(v).toLocaleString()}`;

export function PlanPositionRoomDetailCard() {
  const { mcpId } = useViewportSelection();
  const inspected = useGraphSelectionStore((s) => s.inspectedEntity);
  const roomKey = inspected?.type === 'room' ? inspected.id : null;

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'room_story'));
  const { data } = useStrategyData<RoomStoryData>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy,
  );
  const room = useMemo(() => data?.data?.rooms.find((r) => r.key === roomKey) ?? null, [data, roomKey]);

  useCardExport(useMemo(() => ({
    content_key: 'plan_position_room_detail',
    status: (data ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: strategy ? ['strategy-data', strategy, { semantic_id: mcpId ?? '' }] : null,
    text: room ? `${room.label}: ${room.requirements_selected} of ${room.requirements_total} decided` : 'No room selected',
    json: room ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [data, mcpId, strategy, room]));

  if (!room) {
    return (
      <div className="p-4">
        <div className={cn(text.muted, 'text-[11.5px] leading-relaxed')}>
          Click a room on the Plan Position canvas to open its read here — tier totals, every undecided
          requirement by spread, and the room&apos;s own narrative.
        </div>
      </div>
    );
  }

  const undecided = room.requirements
    .filter((r) => r.spread_type === 'undecided' && r.candidate_price_spread)
    .map((r) => ({ req: r.requirement, spread: r.candidate_price_spread!.max - r.candidate_price_spread!.min }))
    .sort((a, b) => b.spread - a.spread);

  return (
    <div className="p-3 flex flex-col gap-2.5">
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <span className="text-[12px] font-bold text-foreground">{room.label}</span>
        </div>
        <div className="px-3 py-2.5 flex flex-col gap-0">
          <div className="flex gap-2 items-baseline py-1 text-[12px]">
            <span className={cn(text.muted, 'flex-1')}>Decided</span>
            <span className="font-mono font-semibold tabular-nums text-foreground">{room.requirements_selected} of {room.requirements_total}</span>
          </div>
          <div className="flex gap-2 items-baseline py-1 border-t border-border text-[12px]">
            <span className={cn(text.muted, 'flex-1')}>Candidates</span>
            <span className="font-mono tabular-nums text-muted-foreground">{room.candidates_total} · {room.vendor_count} vendors</span>
          </div>
          {room.tier_totals.budget != null && (
            <>
              <div className={cn(text.eyebrow, 'pt-2 pb-0.5')}>Tier totals — furnishing every requirement at that tier</div>
              <div className="flex gap-2 items-baseline py-1 text-[12px]">
                <span className={cn(text.muted, 'flex-1')}>Budget</span>
                <span className="font-mono tabular-nums text-muted-foreground">{money(room.tier_totals.budget)}</span>
              </div>
              <div className="flex gap-2 items-baseline py-1 border-t border-border text-[12px]">
                <span className={cn(text.muted, 'flex-1')}>Mid</span>
                <span className="font-mono font-semibold tabular-nums text-foreground">{money(room.tier_totals.mid!)}</span>
              </div>
              <div className="flex gap-2 items-baseline py-1 border-t border-border text-[12px]">
                <span className={cn(text.muted, 'flex-1')}>Premium</span>
                <span className="font-mono tabular-nums text-muted-foreground">{money(room.tier_totals.premium!)}</span>
              </div>
            </>
          )}
          {undecided.length > 0 && (
            <>
              <div className={cn(text.eyebrow, 'pt-2 pb-0.5')}>Undecided, by spread</div>
              {undecided.map((u) => (
                <div key={u.req} className="flex gap-2 items-baseline py-1 border-t border-border text-[12px]">
                  <span className={cn(text.muted, 'flex-1 truncate')}>{u.req}</span>
                  <span className="font-mono font-semibold tabular-nums text-amber-400">{money(u.spread)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {room.insight && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <span className="text-[12px] font-bold text-foreground">Read</span>
            <span className="ml-auto text-[9px] font-mono uppercase tracking-wide text-muted-foreground border border-border rounded-full px-1.5">room_story</span>
          </div>
          <div className="px-3 py-2.5">
            <p className={cn(text.body, 'leading-relaxed text-[12px]')}>{room.insight}</p>
          </div>
        </div>
      )}
    </div>
  );
}
